import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useParams, useNavigate, useLocation } from "react-router-dom";

const ActionablesByStatus = () => {
  const { status } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const normalizeStatus = (value) => {
    if (!value) return value;

    const map = {
      Upcoming: "New",
      "Not Completed": "Pending",
      "Completed Late": "Completed After Deadline",
      New: "New",
      Pending: "Pending",
      Completed: "Completed",
      "Completed After Deadline": "Completed After Deadline",
    };

    return map[value] || value;
  };

  const normalizedStatus = normalizeStatus(status);

  const queryParams = useMemo(() => {
    const qs = new URLSearchParams(location.search);
    return {
      date: qs.get("date") || "",
      fromMonth: qs.get("fromMonth") || "",
      fromYear: qs.get("fromYear") || new Date().getFullYear(),
      toMonth: qs.get("toMonth") || "",
      toYear: qs.get("toYear") || new Date().getFullYear(),
      location: qs.get("location") || "",
      type: qs.get("type") || "",
      frequency: qs.get("frequency") || "",
      policy: qs.get("policy") || "",
    };
  }, [location.search]);

  const [filters, setFilters] = useState({
    endDate: queryParams.date,
    fromMonth: queryParams.fromMonth,
    fromYear: queryParams.fromYear,
    toMonth: queryParams.toMonth,
    toYear: queryParams.toYear,
    location: queryParams.location,
    frequency: queryParams.frequency,
  });

  const [locations, setLocations] = useState([]);
  const [frequencies, setFrequencies] = useState([]);
  const [actionables, setActionables] = useState([]);
  const [loading, setLoading] = useState(false);
  const [username, setUsername] = useState(localStorage.getItem("username") || "");

  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      endDate: queryParams.date,
      fromMonth: queryParams.fromMonth,
      fromYear: queryParams.fromYear,
      toMonth: queryParams.toMonth,
      toYear: queryParams.toYear,
      location: queryParams.location,
      frequency: queryParams.frequency,
    }));
  }, [queryParams]);

  useEffect(() => {
    const token = localStorage.getItem("accessToken");
    if (!token) {
      setUsername("");
      return;
    }

    axios
      .get("/api/users/me/", {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        setUsername(res.data.username);
        localStorage.setItem("username", res.data.username);
      })
      .catch((err) => console.error("Failed to load username:", err));
  }, []);

  useEffect(() => {
    axios
      .get("/api/actionables/filters/")
      .then((res) => {
        setLocations(res.data.locations || []);
        setFrequencies(res.data.frequencies || []);
      })
      .catch((err) => console.error("Error fetching filters:", err));
  }, []);

  const fetchActionables = async () => {
    if (!username) return;

    setLoading(true);

    try {
      const token = localStorage.getItem("accessToken");

      if (filters.endDate) {
        const url = `/api/actionables/by-date-status/?date=${encodeURIComponent(
          filters.endDate
        )}&status=${encodeURIComponent(normalizedStatus)}`;

        const res = await axios.get(url, {
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        });

        const items = Array.isArray(res.data?.items) ? res.data.items : [];
        setActionables(items);
      } else {
        const payload = {
          username,
          status: normalizedStatus,
          fromMonth: filters.fromMonth,
          fromYear: filters.fromYear,
          toMonth: filters.toMonth,
          toYear: filters.toYear,
          location: filters.location,
          frequency: filters.frequency,
        };

        if (queryParams.policy) {
          payload.policy_or_document_name = queryParams.policy;
        }

        const res = await axios.post("/api/actionables/by-status/", payload, {
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        });

        setActionables(res.data.actionables || []);
      }
    } catch (err) {
      console.error("Error loading actionables by status:", err);
      setActionables([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (username) {
      fetchActionables();
    }
  }, [
    username,
    normalizedStatus,
    filters.endDate,
    filters.fromMonth,
    filters.fromYear,
    filters.toMonth,
    filters.toYear,
    filters.location,
    filters.frequency,
  ]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const buildDetailHref = (item) => {
    const idOrUat =
      item.uat_id || item.id || item.master_id || item.actionable_number;
    return `/actionable/${encodeURIComponent(idOrUat)}`;
  };

  return (
    <div className="container">
      <div className="compact-hero">
        <div className="header-row" style={{ marginBottom: 0 }}>
          <div>
            <h1 className="header">
              Actionables - {normalizedStatus}{" "}
              {filters.endDate ? `on ${filters.endDate}` : ""}
            </h1>
            <p
              style={{
                margin: "10px 0 0",
                color: "rgba(255,255,255,0.82)",
                lineHeight: 1.7,
              }}
            >
              Filter the register by date, location, or frequency and drill into
              each actionable from the status list.
            </p>
          </div>
          <button className="back-button" onClick={() => navigate(-1)}>
            Back
          </button>
        </div>
      </div>

      <div style={{ marginBottom: 14 }}>
        <strong>User:</strong> <span className="tag">{username || "Not signed in"}</span>
      </div>

      <div className="filters">
        <div className="filter-item">
          <label>End Date</label>
          <input
            type="date"
            name="endDate"
            value={filters.endDate || ""}
            onChange={handleFilterChange}
          />
        </div>

        <div className="filter-item">
          <label>From Month</label>
          <select
            name="fromMonth"
            value={filters.fromMonth}
            onChange={handleFilterChange}
            disabled={Boolean(filters.endDate)}
          >
            <option value="">Select</option>
            {monthNames.map((month) => (
              <option key={month} value={month}>
                {month}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-item">
          <label>From Year</label>
          <input
            type="number"
            name="fromYear"
            value={filters.fromYear}
            onChange={handleFilterChange}
            disabled={Boolean(filters.endDate)}
          />
        </div>

        <div className="filter-item">
          <label>To Month</label>
          <select
            name="toMonth"
            value={filters.toMonth}
            onChange={handleFilterChange}
            disabled={Boolean(filters.endDate)}
          >
            <option value="">Select</option>
            {monthNames.map((month) => (
              <option key={month} value={month}>
                {month}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-item">
          <label>To Year</label>
          <input
            type="number"
            name="toYear"
            value={filters.toYear}
            onChange={handleFilterChange}
            disabled={Boolean(filters.endDate)}
          />
        </div>

        <div className="filter-item">
          <label>Location</label>
          <select
            name="location"
            value={filters.location}
            onChange={handleFilterChange}
          >
            <option value="">All</option>
            {locations.map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-item">
          <label>Frequency</label>
          <select
            name="frequency"
            value={filters.frequency}
            onChange={handleFilterChange}
          >
            <option value="">All</option>
            {frequencies.map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-item">
          <button className="apply-button" onClick={fetchActionables}>
            Apply Filters
          </button>
        </div>
      </div>

      <div
        className="metric-grid"
        style={{ gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", marginBottom: "1.4rem" }}
      >
        {[
          { label: "Status", value: normalizedStatus },
          { label: "Visible Rows", value: actionables.length },
          { label: "Date Filter", value: filters.endDate || "Range" },
        ].map((item) => (
          <div key={item.label} className="surface-card" style={{ padding: "1.2rem" }}>
            <div style={{ fontSize: "0.82rem", color: "#65748f", fontWeight: 700 }}>
              {item.label}
            </div>
            <div style={{ marginTop: "8px", fontSize: "1.35rem", fontWeight: 800, color: "#102a71" }}>
              {item.value}
            </div>
          </div>
        ))}
      </div>

      <div className="card">
        {loading ? (
          <p>Loading...</p>
        ) : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Actionable Number</th>
                  <th>Name</th>
                  <th>Location</th>
                  <th>Type</th>
                  <th>Category</th>
                  <th>Frequency</th>
                  <th>Start Date</th>
                  <th>End Date</th>
                  <th>Status</th>
                  <th>Description</th>
                  <th>Owner</th>
                  <th>Policy / Document</th>
                </tr>
              </thead>
              <tbody>
                {actionables.length === 0 ? (
                  <tr>
                    <td colSpan="12" className="no-data">
                      No data found.
                    </td>
                  </tr>
                ) : (
                  actionables.map((item, index) => (
                    <tr key={index}>
                      <td>
                        <a href={buildDetailHref(item)} className="link">
                          {item.actionable_number}
                        </a>
                      </td>
                      <td>{item.actionable_name}</td>
                      <td>{item.location}</td>
                      <td>{item.type}</td>
                      <td>{item.category}</td>
                      <td>{item.frequency}</td>
                      <td>{item.start_date}</td>
                      <td>{item.end_date}</td>
                      <td>{item.status}</td>
                      <td>{item.actionable_description}</td>
                      <td>{item.username}</td>
                      <td>{item.policy_or_document_name}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <style>{`
        .container {
          padding-top: 24px;
        }

        .header-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 18px;
        }

        .filter-item {
          display: flex;
          flex-direction: column;
        }

        .filter-item label {
          margin-bottom: 6px;
        }

        .link {
          text-decoration: none;
        }

        .no-data {
          text-align: center;
          padding: 15px;
          font-style: italic;
        }
      `}</style>
    </div>
  );
};

export default ActionablesByStatus;
